/**
 * PaymentService
 * 
 * Serviço para gerenciar pagamentos e assinaturas
 * - Criar assinaturas
 * - Tokenizar cartões (PCI DSS compliant)
 * - Upgrade/Downgrade
 * - Cancelamento
 * 
 * Fase 5 - UI de Pagamentos
 */

import { supabase } from '../lib/supabase';

// Tipos
export interface CreateSubscriptionPayload {
  plan_id: string;
  billing_cycle: 'monthly' | 'semester' | 'annual' | 'test3d';
  payment_method: 'credit_card' | 'boleto';
  customer: {
    name: string;
    email: string;
    document: string;
    phone: string;
    address: {
      line_1: string;
      zip_code: string;
      city: string;
      state: string;
      country: string;
    };
  };
  payment_data: {
    card_token?: string;
    boleto?: {
      instructions?: string;
      due_days?: number;
    };
  };
}

export interface CreateSubscriptionResponse {
  success: boolean;
  subscription_id: string;
  status: 'active' | 'pending';
  payment_method: 'credit_card' | 'boleto';
  boleto_url?: string;
  boleto_barcode?: string;
  boleto_due_date?: string;
  message?: string;
  scheduled?: boolean;
  effective_at?: string;
}

export interface CardData {
  number: string;
  holder_name: string;
  exp_month: string;
  exp_year: string;
  cvv: string;
}

export interface TokenizeCardResponse {
  id: string;
  type: string;
  expires_at: string;
}

class PaymentServiceClass {
  private pagarmePublicKey: string;

  constructor() {
    // Detectar ambiente (sandbox ou production)
    const environment = import.meta.env.VITE_PAGARME_ENVIRONMENT || 'sandbox';
    const isProduction = environment === 'production';
    
    // Selecionar Public Key baseada no ambiente
    this.pagarmePublicKey = isProduction
      ? import.meta.env.VITE_PAGARME_PUBLIC_KEY_LIVE || ''
      : import.meta.env.VITE_PAGARME_PUBLIC_KEY || '';
    
    // Log do ambiente (apenas em desenvolvimento)
    if (import.meta.env.DEV) {
      console.log(`🌍 PaymentService - Ambiente: ${environment}`);
      console.log(`🔑 Usando chave: ${this.pagarmePublicKey.substring(0, 10)}...`);
    }
  }

  /**
   * Tokenizar cartão de crédito (Frontend apenas)
   * ⚠️ Token expira em 60 segundos!
   */
  async tokenizeCard(cardData: CardData): Promise<string> {
    try {
      const response = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${this.pagarmePublicKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'card',
          card: {
            number: cardData.number.replace(/\s/g, ''),
            holder_name: cardData.holder_name,
            exp_month: parseInt(cardData.exp_month),
            exp_year: parseInt(cardData.exp_year),
            cvv: cardData.cvv
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao tokenizar cartão');
      }

      const data: TokenizeCardResponse = await response.json();

      return data.id;
    } catch (error: any) {
      console.error('Erro ao tokenizar cartão:', error);
      throw new Error('Falha ao processar cartão. Verifique os dados e tente novamente.');
    }
  }

  /**
   * Criar assinatura via Edge Function
   * Suporta cartão de crédito e boleto bancário
   */
  async createSubscription(payload: CreateSubscriptionPayload): Promise<CreateSubscriptionResponse> {
    try {
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: payload
      });

      if (error) {
        // 🔍 DEBUG: Ler o corpo da resposta se for um Response object
        let errorDetails = null;
        
        if (error.context instanceof Response) {
          try {
            const responseText = await error.context.text();
            console.error('🔴 Response body:', responseText);
            
            try {
              errorDetails = JSON.parse(responseText);
              console.error('🔴 Error details:', errorDetails);
            } catch {
              errorDetails = { error: responseText };
            }
          } catch (readError) {
            console.error('❌ Erro ao ler response:', readError);
          }
        }
        
        // Extrair mensagem de erro
        let errorMessage = 'Erro ao criar assinatura';
        
        if (errorDetails?.error) {
          errorMessage = errorDetails.error;
        } else if (errorDetails?.message) {
          errorMessage = errorDetails.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        console.error('🔴 Final error message:', errorMessage);
        throw new Error(errorMessage);
      }

      return data as CreateSubscriptionResponse;
    } catch (error: any) {
      console.error('Erro ao criar assinatura:', error);
      throw error; // Propagar o erro original
    }
  }

  /**
   * Cancelar assinatura
   * 
   * ✅ CORRIGIDO 27/01/2026:
   * Agora cancela corretamente na Pagar.me via Edge Function
   * O webhook 'subscription.canceled' atualiza o status no Supabase automaticamente
   * 
   * Fluxo:
   * 1. Chama Edge Function cancel-subscription
   * 2. Edge Function cancela na Pagar.me (DELETE /subscriptions/{id})
   * 3. Pagar.me envia webhook 'subscription.canceled'
   * 4. Webhook atualiza status='canceled' no Supabase (mantém active_until)
   */
  async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      console.log('🛑 Cancelando assinatura:', subscriptionId);
      
      // Chamar Edge Function para cancelar na Pagar.me
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscription_id: subscriptionId }
      });

      if (error) {
        console.error('❌ Erro ao cancelar via Edge Function:', error);
        
        // Extrair mensagem de erro
        let errorMessage = 'Erro ao cancelar assinatura';
        
        if (error.context instanceof Response) {
          try {
            const responseText = await error.context.text();
            const errorDetails = JSON.parse(responseText);
            errorMessage = errorDetails.error || errorDetails.message || errorMessage;
          } catch {
            // Não conseguiu parsear, usar mensagem padrão
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        throw new Error(errorMessage);
      }

      console.log('✅ Assinatura cancelada com sucesso:', data);
      
      // O webhook irá atualizar o status automaticamente
      // Não precisa fazer UPDATE manual no Supabase
      
    } catch (error: any) {
      console.error('Erro ao cancelar assinatura:', error);
      throw error; // Propagar o erro original
    }
  }

  /**
   * Fazer upgrade/downgrade de plano
   */
  async changePlan(newPlanTier: 'basic' | 'premium', billingCycle: 'monthly' | 'semester' | 'annual'): Promise<void> {
    try {
      // Buscar user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Atualizar plano no Supabase
      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          plan_tier: newPlanTier,
          billing_cycle: billingCycle,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // TODO: Comunicar mudança para Pagar.me (se necessário cobrar diferença)
    } catch (error: any) {
      console.error('Erro ao alterar plano:', error);
      throw new Error('Erro ao alterar plano');
    }
  }

  /**
   * Obter planos disponíveis
   */
  async getAvailablePlans() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans_public')
        .select('*')
        .order('tier');

      if (error) throw error;

      return data;
    } catch (error: any) {
      console.error('Erro ao buscar planos:', error);
      throw new Error('Erro ao buscar planos');
    }
  }

  /**
   * Obter histórico de transações
   */
  async getTransactionHistory(userId: string) {
    try {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      return data;
    } catch (error: any) {
      console.error('Erro ao buscar histórico:', error);
      throw new Error('Erro ao buscar histórico');
    }
  }

  /**
   * Validar se token ainda é válido (< 55s)
   */
  isTokenValid(tokenTimestamp: number): boolean {
    const age = Date.now() - tokenTimestamp;
    return age < 55000; // 55 segundos de margem
  }

  /**
   * Formatar número de cartão (xxxx xxxx xxxx xxxx)
   */
  formatCardNumber(value: string): string {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g) || [];
    return chunks.join(' ');
  }

  /**
   * Formatar data de expiração (MM/YY)
   */
  formatExpiry(value: string): string {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  }

  /**
   * Validar número de cartão (Luhn algorithm)
   */
  validateCardNumber(cardNumber: string): boolean {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (!/^\d{13,19}$/.test(cleaned)) return false;

    let sum = 0;
    let isEven = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  /**
   * Validar CVV
   */
  validateCVV(cvv: string): boolean {
    return /^\d{3,4}$/.test(cvv);
  }

  /**
   * Validar data de expiração
   */
  validateExpiry(month: string, year: string): boolean {
    const now = new Date();
    const currentYear = now.getFullYear() % 100; // Últimos 2 dígitos
    const currentMonth = now.getMonth() + 1;

    const expMonth = parseInt(month);
    const expYear = parseInt(year);

    if (expMonth < 1 || expMonth > 12) return false;
    if (expYear < currentYear) return false;
    if (expYear === currentYear && expMonth < currentMonth) return false;

    return true;
  }

  /**
   * Agendar downgrade de plano (Premium -> Basic ou Basic -> Free)
   *
   * Não abre formulário de pagamento. Invoca a Edge Function
   * 'create-subscription' com payment_method='boleto' para passar
   * pelas validações sem exigir cartão, confiando na detecção
   * antecipada de downgrade já implementada na Edge.
   */
  async scheduleDowngrade(
    targetPlanTier: 'basic' | 'free',
    billingCycle: 'monthly' | 'semester' | 'annual' | 'test3d'
  ): Promise<any> {
    try {
      // 1) Obter plano pelo tier (evita IDs hard-coded)
      const { data: plan, error: planErr } = await supabase
        .from('subscription_plans_public')
        .select('id, tier, active')
        .eq('tier', targetPlanTier)
        .eq('active', true)
        .single();

      if (planErr || !plan?.id) {
        throw new Error('Plano não encontrado para o downgrade');
      }

      // 2) Montar payload mínimo
      const payload: any = {
        plan_id: plan.id,
        billing_cycle: billingCycle,
        payment_method: 'boleto', // evita exigir card_token
        payment_data: {} // ignorado no caminho de downgrade
      };

      // 3) Invocar Edge Function
      const { data, error } = await supabase.functions.invoke('create-subscription', {
        body: payload
      });

      if (error) {
        // Tentar extrair detalhes
        let msg = 'Erro ao agendar downgrade';
        if (error.context instanceof Response) {
          try {
            const txt = await error.context.text();
            try {
              const j = JSON.parse(txt);
              msg = j.error || j.message || msg;
            } catch {
              msg = txt || msg;
            }
          } catch {
            // Silenciosamente ignorar erro ao ler contexto
          }
        } else if (error.message) {
          msg = error.message;
        }
        throw new Error(msg);
      }
      // Esperado: { success: true, scheduled: true, effective_at, message }
      return data;
    } catch (e: any) {
      console.error('Erro em scheduleDowngrade:', e);
      throw e;
    }
  }
}

export const PaymentService = new PaymentServiceClass();
